import { Breakpoint, ButtonType, Typography, UiComponentSize } from '@altibox/design-system-component-lib';
import React, { FunctionComponent } from 'react';

import { ImageProps } from 'src/components/image';
import { InformationStep } from 'src/components/information-step';
import { ProductInformationStep } from 'src/components/information-step/product-information-step';
import { Link } from 'src/components/link';
import { ReceiptCard } from 'src/components/receipt-card';
import { config } from 'src/config';
import { i18n } from 'src/i18n';
import { emailImageProps, heartImageProps, mapPinImageProps, serverImageProps } from 'src/resources/images';
import { navigationService } from 'src/services/navigation-service';
import {
  selectHasOrderProcessStarted,
  selectOrderFailed,
  selectSelectedProductOrThrow,
} from 'src/state/order/selectors';
import { selectBonusBandwidthStatusOrThrow } from 'src/state/request/selectors';
import { useAppSelector } from 'src/state/thunk-api';
import { I18nUtils } from 'src/utils/i18n-utils';

import {
  StyledButton,
  StyledContainer,
  StyledHeading,
  StyledHeadingPlaceholder,
  StyledSuccessItems,
  StyledTitle,
} from './page-success.styled';

interface SuccessItem {
  icon: ImageProps;
  title: string;
  description: React.ReactNode;
}

export const PageSuccess: FunctionComponent = () => {
  const bonusBandwidthStatus = useAppSelector(selectBonusBandwidthStatusOrThrow);
  const loading = useAppSelector(selectHasOrderProcessStarted);
  const hasOrderFailed = useAppSelector(selectOrderFailed);
  const selectedProduct = useAppSelector(selectSelectedProductOrThrow);

  const { pricing } = selectedProduct;

  if (hasOrderFailed) {
    return null;
  }

  const renderSuccessItem = (item: SuccessItem, index: number): React.ReactNode => (
    <InformationStep key={index} icon={item.icon} loading={loading}>
      <StyledTitle component="p" variant="paragraph1" maxBreakpoint={Breakpoint.TABLET}>
        {item.title}
      </StyledTitle>
      <Typography variant="paragraph2" component="p" maxBreakpoint={Breakpoint.TABLET}>
        {item.description}
      </Typography>
    </InformationStep>
  );

  const renderSuccessItems = (): React.ReactNode => {
    const items: SuccessItem[] = [
      ...(pricing.deliveryRequired
        ? [
            {
              icon: mapPinImageProps,
              title: i18n.page.success.items.delivery.title,
              description: i18n.page.success.items.delivery.description,
            },
          ]
        : []),
      {
        icon: serverImageProps,
        title: i18n.page.success.items.setup.title,
        description: I18nUtils.richMessage(i18n.page.success.items.setup.description[bonusBandwidthStatus], {
          productName: selectedProduct.name,
          linkToGetStarted: (
            <Link variant="paragraph2" maxBreakpoint={Breakpoint.TABLET} href={config.GET_STARTED_URL}>
              {i18n.page.success.items.setup.linkToGetStarted}
            </Link>
          ),
          linkToCustomerService: (
            <Link variant="paragraph2" maxBreakpoint={Breakpoint.TABLET} href={config.CUSTOMER_SERVICE_URL}>
              {i18n.page.success.items.setup.linkToCustomerService}
            </Link>
          ),
        }),
      },
      {
        icon: heartImageProps,
        title: I18nUtils.message(i18n.page.success.items.learnMore.title, {
          productName: selectedProduct.shortName ?? selectedProduct.name,
        }),
        description: I18nUtils.richMessage(i18n.page.success.items.learnMore.description, {
          productName: selectedProduct.name,
          linkToLearnMorePage: (
            <Link variant="paragraph2" maxBreakpoint={Breakpoint.TABLET} href={config.CUSTOMER_SERVICE_XBOX_URL}>
              {i18n.page.success.items.learnMore.linkToLearnMorePage}
            </Link>
          ),
        }),
      },
    ];

    return items.map(renderSuccessItem);
  };

  const handleButtonClick = () => navigationService.goToMineSider();

  return (
    <StyledContainer>
      {loading ? (
        <StyledHeadingPlaceholder />
      ) : (
        <StyledHeading component="h1" variant="headline2">
          {i18n.page.success.title}
        </StyledHeading>
      )}
      <StyledSuccessItems>
        <ProductInformationStep icon={emailImageProps} loading={loading}>
          <ReceiptCard />
        </ProductInformationStep>
        {renderSuccessItems()}
      </StyledSuccessItems>
      {!loading && (
        <StyledButton
          buttonType={ButtonType.PRIMARY_A}
          uiComponentSize={UiComponentSize.LARGE}
          onClick={handleButtonClick}
        >
          {i18n.page.success.buttonText}
        </StyledButton>
      )}
    </StyledContainer>
  );
};
